import { CalculationResult } from "../../types/calculations";
import {
  ArmorConfiguration,
  Hull,
  HullConfiguration,
  HullOption,
} from "../../types/ship";
import { SpecializedHull, AdditionalHull } from "../../types/hull";
import {
  HULL,
  HULL_OPTIONS,
  SPECIALIZED_HULL_TYPES,
  ADDITIONAL_HULL_TYPES,
} from "../../constants/hull";
import { ARMOR_TYPES } from "../../constants/armor";

/**
 * Input parameters for calculating hull and armor stats.
 */
export interface HullAndArmorStatsInput {
  shipSize: number;
  hull: Hull | null;
  armor: ArmorConfiguration | null;
  hullConfiguration: HullConfiguration | null;
  hullOptions: HullOption[];
  specializedHull: SpecializedHull | null;
  additionalHull: AdditionalHull | null;
  spunHullPercentage?: number; // For Double Hull
  stealthTier?: string; // For Stealth option
  techLevel: number; // Add techLevel for calculations
}

/**
 * Detailed breakdown of the hull and armor stats.
 */
export interface HullAndArmorStatsOutput {
  // Cost
  totalCost: number;
  baseHullCost: number;
  modifiedHullCost: number;
  optionsCost: number;
  armorCost: number;
  // Hull Points
  hullPoints: number;
  // Volume
  totalConsumedVolume: number;
  additionalHullVolume: number;
  stealthVolume: number;
  armorVolume: number;
  // Solar Coating Power
  solarCoatingPower?: number;
}

/**
 * Calculates the power output of Solar Coating per Traveller ruleset.
 * @param shipSize - The ship size in tons
 * @param percent - Percent of hull coated (max 40)
 * @param hullType - Hull configuration type
 * @param techLevel - Ship tech level
 * @returns Power output in Power units
 */
export function calculateSolarCoatingPowerOutput(
  shipSize: number,
  percent: number,
  hullType: string,
  techLevel: number,
): number {
  const allowed = [
    "standard",
    "sphere",
    "closeStructure",
    "dispersedStructure",
  ];
  if (!allowed.includes(hullType)) return 0;
  if (percent <= 0 || percent > 40) return 0;
  // Use powerPerUnit table from HULL_OPTIONS
  const powerTable = HULL_OPTIONS.solarCoating.powerPerUnit as Record<
    number,
    number
  >;
  // Find the highest TL <= techLevel
  const tls = Object.keys(powerTable)
    .map(Number)
    .filter((tl) => tl <= techLevel);
  const bestTL = tls.length > 0 ? Math.max(...tls) : 0;
  const powerPerUnit = powerTable[bestTL] || 0;
  return percent * powerPerUnit;
}

export function calculateSolarCoatingCost(
  percent: number,
  techLevel: number,
): number {
  const costTable = HULL_OPTIONS.solarCoating.costPerUnit as Record<
    number,
    number
  >;
  const tls = Object.keys(costTable)
    .map(Number)
    .filter((tl) => tl <= techLevel);
  const bestTL = tls.length > 0 ? Math.max(...tls) : 0;
  const costPerUnit = costTable[bestTL] || 0;
  return percent * costPerUnit;
}

export function getBestPowerForTechLevel(powerTable: Record<number, number>, techLevel: number, tlOverrideActive = false) {
  // Find the highest TL <= techLevel, or all if override
  const tls = Object.keys(powerTable)
    .map(Number)
    .filter((tl) => tlOverrideActive || tl <= techLevel);
  const bestTL = tls.length > 0 ? Math.max(...tls) : 0;
  return powerTable[bestTL] || 0;
}

export function getBestCostForTechLevel(costTable: Record<number, number>, techLevel: number, tlOverrideActive = false) {
  // Find the highest TL <= techLevel, or all if override
  const tls = Object.keys(costTable)
    .map(Number)
    .filter((tl) => tlOverrideActive || tl <= techLevel);
  const bestTL = tls.length > 0 ? Math.max(...tls) : 0;
  return costTable[bestTL] || 0;
}

/**
 * Calculates the total cost, volume, and hull points of the hull and armor.
 */
export const calculateHullAndArmorStats = (
  input: HullAndArmorStatsInput,
): CalculationResult<
  HullAndArmorStatsOutput & { solarCoatingPower?: number }
> => {
  try {
    const {
      shipSize,
      hullConfiguration,
      hullOptions,
      specializedHull,
      additionalHull,
      spunHullPercentage,
      stealthTier,
      armor,
    } = input;

    if (!hullConfiguration) {
      return {
        success: true,
        data: {
          totalCost: 0,
          baseHullCost: 0,
          modifiedHullCost: 0,
          optionsCost: 0,
          armorCost: 0,
          hullPoints: 0,
          totalConsumedVolume: 0,
          additionalHullVolume: 0,
          stealthVolume: 0,
          armorVolume: 0,
          solarCoatingPower: 0,
        },
      };
    }

    // --- Cost Calculation ---
    // 1. Base Hull Cost
    let baseHullCost =
      hullConfiguration.type === "planetoid" ||
      hullConfiguration.type === "bufferedPlanetoid"
        ? shipSize * (hullConfiguration.costPerTon || 4000)
        : shipSize * HULL.BASE_COST;
    // 2. Modifiers
    let modifiedHullCost = baseHullCost * (hullConfiguration.costModifier || 1);
    if (specializedHull) {
      modifiedHullCost *=
        SPECIALIZED_HULL_TYPES[
          specializedHull.type as keyof typeof SPECIALIZED_HULL_TYPES
        ].costModifier || 1;
    }
    if (additionalHull?.type === "doubleHull" && spunHullPercentage) {
      modifiedHullCost *=
        1 + ADDITIONAL_HULL_TYPES.doubleHull.costModifier * spunHullPercentage;
    }
    // 3. Options Cost
    let optionsCost = 0;
    let solarCoatingPower = 0;
    for (const option of hullOptions) {
      const optionData = HULL_OPTIONS[option.type as keyof typeof HULL_OPTIONS];
      if (option.type === "stealth" && stealthTier) {
        const stealthData = (optionData as any).types.find(
          (t: any) => t.type === stealthTier,
        );
        if (stealthData)
          optionsCost += shipSize * (stealthData.costPerTon || 0);
      } else if (option.type === "solarCoating") {
        // Solar Coating: cost and power per percent, based on TL
        const percent = typeof option.percent === "number" ? option.percent : 0;
        optionsCost += calculateSolarCoatingCost(percent, input.techLevel);
        solarCoatingPower = calculateSolarCoatingPowerOutput(
          shipSize,
          percent,
          hullConfiguration.type,
          input.techLevel,
        );
      } else if (optionData.costPerTon) {
        optionsCost += shipSize * optionData.costPerTon;
      }
    }
    if (additionalHull?.costPerTon) {
      optionsCost += shipSize * additionalHull.costPerTon;
    }
    // 4. Armor Cost
    let armorCost = 0;
    if (armor) {
      const armorData = ARMOR_TYPES[armor.type as keyof typeof ARMOR_TYPES];
      if (armorData) {
        // Get armor volume modifier from hull configuration
        const armorVolumeModifier = hullConfiguration.armorVolumeModifier || 1;

        // Calculate armor tonnage consumed (adjusted for hull type)
        const armorTonnage = shipSize * armorData.tonnageConsumed * armorVolumeModifier;

        // Apply size multipliers based on ship size
        let sizeMultiplier = 1;
        if (shipSize >= 5 && shipSize <= 15) sizeMultiplier = 4;
        else if (shipSize >= 16 && shipSize <= 25) sizeMultiplier = 3;
        else if (shipSize >= 26 && shipSize <= 99) sizeMultiplier = 2;
        // 100+ tons uses multiplier = 1

        // Armor cost = Armor Tonnage × Cost Per Ton × Armor Rating × Size Multiplier
        armorCost = armorTonnage * armorData.costPerTon * armor.rating * sizeMultiplier;
      }
    }
    const totalCost = modifiedHullCost + optionsCost + armorCost;

    // --- Hull Points Calculation ---
    let hullPoints = 0;
    if (shipSize >= 100000) hullPoints = shipSize / 1.5;
    else if (shipSize >= 25000) hullPoints = shipSize / 2;
    else hullPoints = shipSize / 2.5;
    hullPoints *= hullConfiguration.hullPointsModifier || 1;
    if (specializedHull?.hullPointsModifier) {
      hullPoints *= specializedHull.hullPointsModifier;
    }

    // --- Volume Calculation ---
    let additionalHullVolume = 0;
    if (additionalHull) {
      const percentage = additionalHull.percent || 0;
      if (
        additionalHull.type === "doubleHull" ||
        additionalHull.type === "hamsterCage"
      ) {
        additionalHullVolume = shipSize * (percentage / 100);
      }
    }
    let stealthVolume = 0;
    const stealthOption = hullOptions.find((o) => o.type === "stealth");
    if (stealthOption?.tier === "basic") {
      const stealthData = HULL_OPTIONS.stealth.types?.find(
        (t) => t.type === "basic",
      );
      if (stealthData?.tonnage) {
        stealthVolume = shipSize * stealthData.tonnage;
      }
    }
    let armorVolume = 0;
    if (armor && armor.rating > 0) {
      const armorData = ARMOR_TYPES[armor.type as keyof typeof ARMOR_TYPES];
      if (armorData) {
        let baseArmorVolume =
          shipSize * armorData.tonnageConsumed * (armor.rating || 0);
        if (hullConfiguration?.armorVolumeModifier) {
          baseArmorVolume *= hullConfiguration.armorVolumeModifier;
        }
        armorVolume = baseArmorVolume;
      }
    }
    const totalConsumedVolume =
      additionalHullVolume + stealthVolume + armorVolume;

    return {
      success: true,
      data: {
        totalCost,
        baseHullCost,
        modifiedHullCost,
        optionsCost,
        armorCost,
        hullPoints,
        totalConsumedVolume,
        additionalHullVolume,
        stealthVolume,
        armorVolume,
        solarCoatingPower,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate hull and armor stats: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};
